import React from 'react';
import { motion } from 'framer-motion';
import { User, MapPin, Calendar } from 'lucide-react';
import { PilgrimGuideLink } from '../../../types/pilgrimGuide';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  pilgrims: PilgrimGuideLink[];
};

export const RecentPilgrims = ({ pilgrims }: Props) => {
  const { translations } = useLanguage();

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <h3 className="font-semibold text-lg mb-6">
        {translations?.recentPilgrims?.title || 'Recent Pilgrims Added'}
      </h3>

      <div className="space-y-4">
        {pilgrims.map((pilgrim) => (
          <motion.div
            key={pilgrim.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
          >
            <div
              className={`p-2 rounded-full ${
                pilgrim.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
              }`}
            >
              <User
                className={
                  pilgrim.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                }
                size={20}
              />
            </div>

            <div className="flex-1">
              <p className="font-medium">{pilgrim.pilgrimName}</p>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <MapPin size={14} />
                  <span>{pilgrim.residenceCountry}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>
                    {new Date(pilgrim.createdAt).toLocaleDateString('en-US')}
                  </span>
                </div>
              </div>
            </div>

            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                pilgrim.status === 'approved'
                  ? 'bg-green-50 text-green-700'
                  : pilgrim.status === 'disputed'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-yellow-50 text-yellow-700'
              }`}
            >
              {pilgrim.status === 'approved'
                ? translations?.recentPilgrims?.statuses?.approved || 'Approved'
                : pilgrim.status === 'disputed'
                ? translations?.recentPilgrims?.statuses?.disputed || 'Disputed'
                : translations?.recentPilgrims?.statuses?.pending || 'Pending'}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
